import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import type { Field, Member, OutboxOp, Task, Version } from './types';

// Rows are stamped with groupId on the way in so one browser can cache several
// team environments without them mixing.
type Scoped<T> = T & { groupId: string };

interface WorkflowDB extends DBSchema {
  tasks: { key: string; value: Scoped<Task>; indexes: { byGroup: string } };
  fields: { key: string; value: Scoped<Field>; indexes: { byGroup: string } };
  versions: { key: string; value: Scoped<Version>; indexes: { byGroup: string } };
  outbox: { key: string; value: OutboxOp; indexes: { byQueuedAt: number } };
  meta: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<WorkflowDB>> | undefined;

function getDb(): Promise<IDBPDatabase<WorkflowDB>> {
  dbPromise ??= openDB<WorkflowDB>('project-workflow', 1, {
    upgrade(db) {
      const tasks = db.createObjectStore('tasks', { keyPath: 'id' });
      tasks.createIndex('byGroup', 'groupId');

      const fields = db.createObjectStore('fields', { keyPath: 'id' });
      fields.createIndex('byGroup', 'groupId');

      const versions = db.createObjectStore('versions', { keyPath: 'id' });
      versions.createIndex('byGroup', 'groupId');

      const outbox = db.createObjectStore('outbox', { keyPath: 'opId' });
      outbox.createIndex('byQueuedAt', 'queuedAt');

      db.createObjectStore('meta');
    },
  });
  return dbPromise;
}

export type CachedSnapshot = {
  rev: number;
  fields: Field[];
  tasks: Task[];
  versions: Version[];
  members: Member[];
};

export async function readSnapshot(groupId: string): Promise<CachedSnapshot> {
  const db = await getDb();
  const [tasks, fields, versions, rev, members] = await Promise.all([
    db.getAllFromIndex('tasks', 'byGroup', groupId),
    db.getAllFromIndex('fields', 'byGroup', groupId),
    db.getAllFromIndex('versions', 'byGroup', groupId),
    db.get('meta', `rev:${groupId}`) as Promise<number | undefined>,
    db.get('meta', `members:${groupId}`) as Promise<Member[] | undefined>,
  ]);

  return {
    rev: rev ?? 0,
    tasks,
    fields,
    versions,
    members: members ?? [],
  };
}

/**
 * Applies a server delta. Soft-deleted rows arrive as tombstones and are dropped
 * from the cache entirely, which is how a client that was offline learns about
 * deletions.
 */
export async function applyDelta(
  groupId: string,
  delta: { rev: number; fields: Field[]; tasks: Task[]; versions: Version[]; members: Member[] },
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['tasks', 'fields', 'versions', 'meta'], 'readwrite');

  for (const task of delta.tasks) {
    if (task.deletedAt) await tx.objectStore('tasks').delete(task.id);
    else await tx.objectStore('tasks').put({ ...task, groupId });
  }

  for (const field of delta.fields) {
    if (field.deletedAt) await tx.objectStore('fields').delete(field.id);
    else await tx.objectStore('fields').put({ ...field, groupId });
  }

  for (const version of delta.versions) {
    if (version.deletedAt) await tx.objectStore('versions').delete(version.id);
    else await tx.objectStore('versions').put({ ...version, groupId });
  }

  await tx.objectStore('meta').put(delta.rev, `rev:${groupId}`);
  await tx.objectStore('meta').put(delta.members, `members:${groupId}`);
  await tx.done;
}

/**
 * Drops this team's cached release history. Called when the delta arrives without
 * versions because the member is no longer allowed to see them: withholding them
 * server-side is only half the job while an older copy still sits in IndexedDB.
 */
export async function clearVersions(groupId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('versions', 'readwrite');
  const store = tx.objectStore('versions');
  for (const key of await store.index('byGroup').getAllKeys(groupId)) {
    await store.delete(key);
  }
  await tx.done;
}

export async function putLocalTask(groupId: string, task: Task): Promise<void> {
  const db = await getDb();
  await db.put('tasks', { ...task, groupId });
}

export async function removeLocalTask(taskId: string): Promise<void> {
  const db = await getDb();
  await db.delete('tasks', taskId);
}

// --- Outbox ----------------------------------------------------------------

export async function listOutbox(): Promise<OutboxOp[]> {
  const db = await getDb();
  return db.getAllFromIndex('outbox', 'byQueuedAt');
}

export async function enqueue(op: OutboxOp): Promise<void> {
  const db = await getDb();
  await db.put('outbox', op);
}

export async function dequeue(opId: string): Promise<void> {
  const db = await getDb();
  await db.delete('outbox', opId);
}

export async function updateOp(op: OutboxOp): Promise<void> {
  const db = await getDb();
  await db.put('outbox', op);
}

/**
 * Finds an unsent update for a task so repeated edits while offline collapse
 * into one operation instead of a replayable chain.
 */
export async function findPendingUpdate(taskId: string): Promise<OutboxOp | undefined> {
  const ops = await listOutbox();
  return ops.find((op) => op.taskId === taskId && op.kind === 'task.update');
}

export async function hasPendingFor(taskId: string): Promise<boolean> {
  const ops = await listOutbox();
  return ops.some((op) => op.taskId === taskId);
}

// --- Session cache ---------------------------------------------------------

export async function readMeta<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  return db.get('meta', key) as Promise<T | undefined>;
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  await db.put('meta', value, key);
}

/** Wipes cached team data on logout so the next person on this browser sees none of it. */
export async function clearAll(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['tasks', 'fields', 'versions', 'outbox', 'meta'], 'readwrite');
  await Promise.all([
    tx.objectStore('tasks').clear(),
    tx.objectStore('fields').clear(),
    tx.objectStore('versions').clear(),
    tx.objectStore('outbox').clear(),
    tx.objectStore('meta').clear(),
  ]);
  await tx.done;
}
